import type { Driver } from '@/types';

/**
 * Builds a driver's display name, falling back when there isn't one.
 *
 * The database stores a single `Driver.name` column; the API mapper splits it
 * into firstName/lastName (see useDriverStateV2), so both halves come back as
 * empty strings when the stored name is blank. Call sites used to interpolate
 * them directly — `${driver.firstName} ${driver.lastName}` — which rendered a
 * lone space and produced headers like "Hello ,". Their own fallbacks never
 * ran, because the ternary guarding them only checked that `driver` existed,
 * not that it had a usable name.
 */
export function getDriverDisplayName(
  driver: Pick<Driver, 'firstName' | 'lastName'> | null | undefined,
  fallback: string
): string {
  const fullName = [driver?.firstName, driver?.lastName]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(' ');

  return fullName || fallback;
}
